<template>
  <div class="configurator-cross-sell-renderer">
    <ConfiguratorTextureCanvas
      v-if="canRenderTexture"
      ref="textureCanvas"
      :resolution="textureResolution"
      :background-color="
        transparentBackgroundForExport
          ? 'transparent'
          : (color && color.value) || 'transparent'
      "
      :glass-diameter="size && size.diameter"
      :glass-height="size && size.height"
      :width="printingArea && printingArea.width"
      :height="printingArea && printingArea.height"
      :printing-area-offset-top="printingArea && printingArea.offsetTop"
      :texture-src="configuration && configuration.url"
      :layers="layers"
      full-texture
      headless
      include-background
      @change="updateTexture"
    />

    <div class="configurator-cross-sell-renderer__3d-view">
      <Configurator3dView
        v-if="texture && gltfUrl"
        ref="canvas"
        :gltf="gltfUrl"
        :style="{ width: '300px', height: '500px' }"
        :texture="texture"
        :camera-position="cameraPosition"
        headless
      />
    </div>
  </div>
</template>

<script>
import ConfiguratorTextureCanvas from './ConfiguratorTextureCanvas';
import Configurator3dView from './Configurator3dView';
import { CameraPosition } from '../../api/static/sizes';
import { Resolution } from '../../utils/MathUtils';
import {
  GlassesGetter,
  prefixType as prefixGlassesType,
} from '../../store/modules/configurator/glasses';

export default {
  name: 'ConfiguratorCrossSellRenderer',
  components: {
    Configurator3dView,
    ConfiguratorTextureCanvas,
  },
  props: {
    size: {
      type: Object,
      required: true,
    },
    configuration: {
      type: Object,
      required: true,
    },
    printingArea: {
      type: Object,
      required: true,
    },
    color: {
      type: Object,
      required: true,
    },
    customizations: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      canRenderTexture: false,
      texture: null,
      cameraPositionKey: CameraPosition.CENTER,
      layers: [],
      textureResolution: Resolution.HIGH_DPI,
      /** When true, background-color is undefined so exported texture is transparent */
      transparentBackgroundForExport: false,
    };
  },
  computed: {
    gltfUrl() {
      return this.size?.gltfUrl || null;
    },
    cameraPosition() {
      return (
        this.size &&
        this.size.cameraPosition &&
        this.size.cameraPosition[this.cameraPositionKey]
      );
    },
    zoneMap() {
      if (!this.configuration || !this.configuration.zones) {
        return {};
      }

      // Get zones for this configuration
      const mapZonesGetter =
        this.$store.getters[prefixGlassesType(GlassesGetter.MAP_ZONES)];
      const zones = mapZonesGetter(this.configuration.zones);

      // Create a map by zone ID
      return zones.reduce((acc, zone) => {
        acc[zone.id] = zone;
        return acc;
      }, {});
    },
  },
  watch: {
    customizations: {
      handler() {
        this.updateLayers();
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * Generate a screenshot of the 3D view
     * @return {Promise<string>} Base64 image
     */
    async generateScreenshot() {
      try {
        // Ensure layers are up to date before rendering
        this.updateLayers();

        // The thumbnail is displayed at ~300x500px, so a WEB (72 DPI) texture
        // is plenty. Rendering it at HIGH_DPI for every compatible product
        // piles up memory on Safari iOS before the user even clicks add-to-cart.
        this.textureResolution = Resolution.WEB;

        // Start rendering
        this.canRenderTexture = true;
        await this.$nextTick();

        // Wait for texture canvas to be ready
        if (this.$refs.textureCanvas) {
          await this.$refs.textureCanvas.renderReady();
        }

        // Update texture
        await this.updateTexture();
        await this.$nextTick();

        // Wait for Configurator3dView to be mounted and canvas ref to be available
        // The 3dView is conditionally rendered based on texture AND gltfUrl
        let retries = 0;
        const maxRetries = 20;
        while (!this.$refs.canvas && retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 50));
          retries++;
        }

        // Generate screenshot from 3D view
        if (!this.$refs.canvas) {
          throw new Error(
            `Canvas ref not available after ${maxRetries} retries (${
              maxRetries * 50
            }ms)`
          );
        }

        const screenshot = await this.$refs.canvas.toBase64();

        return screenshot;
      } finally {
        // Cleanup
        this.canRenderTexture = false;
      }
    },

    /**
     * Generate all exports needed for add to cart (textures + 4 camera angles)
     * @returns {Promise<Object>} Object with texture72, texture300, renderLeft, renderCenter, renderRight, renderBack
     */
    async generateAllExports() {
      try {
        // [DEBUG 518999]
        console.info('[518999] generateAllExports start', {
          size: this.size && this.size.name,
          gltfUrl: this.gltfUrl,
        });

        // Ensure layers are up to date before rendering
        this.updateLayers();

        // Transparent background for exported textures (no solid color behind)
        this.transparentBackgroundForExport = true;
        await this.$nextTick();

        // Start rendering
        this.canRenderTexture = true;
        await this.$nextTick();

        // Wait for texture canvas to be ready
        if (this.$refs.textureCanvas) {
          await this.$refs.textureCanvas.renderReady();
        }

        // Update texture
        await this.updateTexture();
        await this.$nextTick();

        // Wait for Configurator3dView to be mounted
        let retries = 0;
        const maxRetries = 20;
        while (!this.$refs.canvas && retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 50));
          retries++;
        }

        if (!this.$refs.canvas) {
          throw new Error(
            `Canvas ref not available after ${maxRetries} retries`
          );
        }

        const exports = {
          texture72: null,
          texture300: null,
          renderLeft: null,
          renderCenter: null,
          renderRight: null,
          renderBack: null,
        };

        // 72 DPI texture (low quality)
        this.textureResolution = Resolution.HIGH_DPI;
        await this.$nextTick();
        if (this.$refs.textureCanvas) {
          await this.$refs.textureCanvas.renderReady();
          exports.texture72 = await this.$refs.textureCanvas.toBlob();
          // [DEBUG 518999]
          console.info('[518999] texture72', {
            blobBytes: exports.texture72 && exports.texture72.size,
          });
        }

        // 300 DPI texture (high quality) - use Resolution.PRINT (360)
        this.textureResolution = Resolution.PRINT;
        await this.$nextTick();
        if (this.$refs.textureCanvas) {
          await this.$refs.textureCanvas.renderReady();
          exports.texture300 = await this.$refs.textureCanvas.toBlob();
          // [DEBUG 518999]
          console.info('[518999] texture300', {
            blobBytes: exports.texture300 && exports.texture300.size,
          });
        }

        // Restore background color for 3D view (so texture has colored background, not transparent)
        // and downscale the resolution: the WebGL material never needs print
        // resolution (only the texture300 blob does). Uploading the 360 DPI
        // texture to the GPU exhausts Safari iOS memory on heavy SVGs and the
        // glass fails to render.
        this.transparentBackgroundForExport = false;
        this.textureResolution = Resolution.HIGH_DPI;
        await this.$nextTick();
        if (this.$refs.textureCanvas) {
          await this.$refs.textureCanvas.renderReady();
        }
        await this.updateTexture();
        // [DEBUG 518999]
        console.info('[518999] 3D texture set', {
          dataUrlLength: this.texture && this.texture.length,
          resolution: this.textureResolution,
        });
        await this.$nextTick();

        // Generate 4 renders with different camera positions
        const cameraPositions = [
          { key: 'renderCenter', position: 'center' },
          { key: 'renderLeft', position: 'left' },
          { key: 'renderRight', position: 'right' },
          { key: 'renderBack', position: 'back' },
        ];

        for (const { key, position } of cameraPositions) {
          this.cameraPositionKey = position;
          await this.$nextTick();

          // [DEBUG 518999]
          const startedAt = performance.now();
          const blob = await this.$refs.canvas.toBlob();
          console.info('[518999] render', key, {
            blobBytes: blob && blob.size,
            ms: Math.round(performance.now() - startedAt),
          });
          exports[key] = blob;
        }

        return exports;
      } finally {
        // Cleanup and reset
        this.transparentBackgroundForExport = false;
        this.cameraPositionKey = 'center';
        this.textureResolution = Resolution.WEB;
        this.canRenderTexture = false;
      }
    },
    async updateTexture() {
      if (this.$refs.textureCanvas) {
        this.texture = await this.$refs.textureCanvas.toBase64();
      }
    },
    updateLayers() {
      const findTypoById =
        this.$store.getters[prefixGlassesType(GlassesGetter.FIND_TYPO_BY_ID)];

      const layers = [];

      // Get zones list for this configuration (sorted by ID to ensure consistent order)
      const zonesArray = Object.values(this.zoneMap).sort(
        (a, b) => a.id - b.id
      );

      // For GENERIC themes, map customizations by index
      // customization[0] → zone[0], customization[1] → zone[1], etc.
      this.customizations.forEach((customizationData, index) => {
        // Handle different formats: might be { customization: {...}, zone: {...} } or just {...}
        let customization, zone;
        if (customizationData.customization) {
          customization = customizationData.customization;
          zone = customizationData.zone; // The zone from the original product
        } else {
          customization = customizationData;
        }

        // Get the corresponding zone by index for the NEW product
        const targetZone = zonesArray[index];

        if (!targetZone) {
          return;
        }

        // Skip if no text, no url, AND no defaultValue
        // If user didn't customize, we use the zone's defaultValue (e.g., "R")
        const hasContent =
          customization.text ||
          customization.url ||
          (zone && zone.defaultValue) ||
          targetZone.defaultValue;

        if (!hasContent) {
          return;
        }

        // Create layer with the target zone and customization
        const layer = {
          ...targetZone,
          _customization: customization,
          typo: (targetZone.typoId && findTypoById(targetZone.typoId)) || null,
        };

        layers.push(layer);
      });

      this.layers = layers;
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-cross-sell-renderer {
  &__3d-view {
    @include sr-only();
  }
}
</style>
